/**
 * Modale plein écran : ouvrir une nouvelle discussion libre avec un contact.
 * Rendue via un PORTAIL (`createPortal` → `document.body`), couleurs en HEX
 * LITTÉRAL (jamais `bg-white`/`text-ink-*`) — même piège que
 * `submit-exercise-modal.tsx` : sous `.theme-ocean`, `bg-white` devient un
 * verre translucide et `ink-*` s'inverse, ce qui rendrait cette carte opaque
 * illisible.
 *
 * ⚠️ AUCUNE saisie de titre : le contact choisi suffit. Le titre est déduit
 * par l'IA du contenu du fil dès le premier message (cf.
 * `discussion-subject.ts` / edge fn `discussion-generate-title`).
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { DiscussionError } from '@/lib/discussions/types';
import { useCreateDiscussion } from '@/lib/discussions/queries';
import type { DocumentDestination } from '@/lib/documents/destinations';

export interface NewDiscussionModalProps {
  /** Rôle de l'utilisateur courant — détermine qui est tutorId/studentId. */
  myRole: 'tutor' | 'student';
  myId: string;
  /** Contacts proposés (déjà filtrés group==='contact' par l'appelant). */
  contacts: readonly DocumentDestination[];
  onClose: () => void;
  onCreated: (discussionId: string) => void;
}

export function NewDiscussionModal({
  myRole,
  myId,
  contacts,
  onClose,
  onCreated,
}: NewDiscussionModalProps) {
  const [peerId, setPeerId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateDiscussion();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!peerId) return;
    setError(null);
    try {
      const discussion = await create.mutateAsync({
        tutorId: myRole === 'tutor' ? myId : peerId,
        studentId: myRole === 'tutor' ? peerId : myId,
        subject: { scope: 'free' },
      });
      onCreated(discussion.id);
    } catch (err) {
      setError(
        err instanceof DiscussionError ? err.message : "La discussion n'a pas pu être créée.",
      );
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex flex-col bg-[#ffffff]"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-between border-b border-[#f4f3f0] px-4 py-3 pt-[max(env(safe-area-inset-top),0.75rem)]">
        <h2 className="font-display text-base font-bold text-[#18160f]">Nouvelle discussion</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center text-[#78746a]"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="flex-1 space-y-4 overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
      >
        {contacts.length === 0 ? (
          <p className="text-sm text-[#78746a]">
            Tu pourras démarrer une discussion dès que tu auras un contact lié.
          </p>
        ) : (
          <>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#5c5850]">
                Avec qui ?
              </span>
              <select
                value={peerId}
                onChange={(e) => setPeerId(e.target.value)}
                required
                className="focus:ring-primary-400 h-11 w-full appearance-none rounded-xl border border-[#e6e4de] bg-[#ffffff] px-3 text-sm text-[#18160f] focus:outline-none focus:ring-2"
              >
                <option value="">Choisir un contact…</option>
                {contacts.map((c) => (
                  <option key={c.key} value={c.target.kind === 'contact' ? c.target.peerId : ''}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>

            {error && (
              <p role="alert" className="text-accent-600 text-sm font-medium">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={!peerId || create.isPending}
              loading={create.isPending}
              className="min-h-[2.75rem] w-full active:scale-[0.98]"
            >
              Démarrer la discussion
            </Button>
          </>
        )}
      </form>
    </div>,
    document.body,
  );
}
